import React, { useEffect, useState } from 'react'
import api from '../../api';
import {
    Container,
    Stack,
    Typography,
    Button,
    TextField,
    Card,
    TableRow,
    TableBody,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    IconButton,
    MenuItem,
    Popover,
    Pagination,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material'
import { toast } from 'react-toastify';
import Iconify from '../iconify/Iconify';
import { useNavigate } from 'react-router-dom';
import CircularProgressWithLabel from '../../utils/loader'
import { VEHICLE_TYPES, VEHICLE_STATUS } from './addVehicle';

const getVehicleTypeLabel = (value) => {
    const type = VEHICLE_TYPES.find((item) => item.value === value);
    return type ? type.label : value;
};

const getStatusLabel = (value) => {
    const status = VEHICLE_STATUS.find((item) => item.value === value || item.key === value);
    return status ? status.label : value;
};

const formatDate = (value) => {
    if (!value) return '-';
    const datePart = String(value).split('T')[0];
    return datePart.includes('-') ? datePart.split('-').reverse().join('-') : datePart;
};

export default function VehiclesComponent() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(null);
    const [showVehicles, setVehicles] = useState([]);
    const [showEditVehicle, setEditVehicle] = useState(null);
    const [showLoader, setShowLoader] = useState(false);

    const [searchPageCount, setSearchPageCount] = useState({
        page: 1,
        total_pages: 0,
        total: 0,
        current_page: 1,
    });
    const [searchQuery, setSearchQuery] = useState({
        search: '',
        status: '',
    });

    const handleOpenMenu = (event) => {
        setOpen(event.currentTarget);
    };
    const handleCloseMenu = () => {
        setOpen(null);
    };

    useEffect(() => {
        getVehicles();
    }, []);

    function getVehicles() {
        setShowLoader(true);
        api.post('/api/v1/vehicles/allVehicles', { page: searchPageCount.page, query: searchQuery })
            .then((response) => {
                if (response.data.success) {
                    setVehicles([...response.data.vehicles]);
                    setSearchPageCount(response.data.pageInfo);
                    setShowLoader(false);
                } else {
                    toast.error(response.data.message);
                    setShowLoader(false);
                }
            })
            .catch(() => {
                toast.error('Something went wrong!');
                setShowLoader(false);
            });
    }

    const viewMoreResult = (event, newPage) => {
        searchPageCount.page = newPage;
        setSearchPageCount({ ...searchPageCount });
        getVehicles();
    };

    const onChangeSearch = (event) => {
        const { name, value } = event.target;
        setSearchQuery((prev) => ({ ...prev, [name]: value }));
    };

    const onClickSearch = () => {
        searchPageCount.page = 1;
        setSearchPageCount({ ...searchPageCount });
        getVehicles();
    };

    const onClickEditVehicle = () => {
        navigate('/add-vehicle', { state: { showEditVehicle } });
    };

    const onClickAddNew = () => {
        navigate('/add-vehicle');
    };

    const onClickAddEditData = (data) => {
        setEditVehicle(data);
    };

    return (
        <>
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        Vehicles
                    </Typography>
                    <Button variant="contained" onClick={onClickAddNew}>
                        Add New Vehicle
                    </Button>
                </Stack>
                <Card style={{ marginBottom: '30px' }}>
                    <Stack direction="row" sx={{ my: 2 }} alignItems="center">
                        <TextField
                            id="outlined-basic"
                            label="Search (registration, make, model)"
                            name="search"
                            variant="outlined"
                            style={{ margin: '10px' }}
                            size="small"
                            fullWidth
                            value={searchQuery.search}
                            onChange={onChangeSearch}
                        />
                        <FormControl fullWidth size="small" style={{ margin: '10px' }}>
                            <InputLabel id="status-filter-label">Status</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                id="status-filter"
                                label="Status"
                                name="status"
                                value={searchQuery.status}
                                onChange={onChangeSearch}
                            >
                                <MenuItem value={''}>All</MenuItem>
                                {VEHICLE_STATUS.map((status) => (
                                    <MenuItem key={status.key} value={status.key}>
                                        {status.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" style={{ margin: '10px' }} onClick={onClickSearch}>
                            Search
                        </Button>
                    </Stack>
                </Card>
                <Card>
                    {showLoader ? (
                        <CircularProgressWithLabel />
                    ) : (
                        <>
                            <TableContainer>
                                <Table stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">Id</TableCell>
                                            <TableCell align="center">Registration No</TableCell>
                                            <TableCell align="center">Type</TableCell>
                                            <TableCell align="center">Make</TableCell>
                                            <TableCell align="center">Model</TableCell>
                                            <TableCell align="center">Color</TableCell>
                                            <TableCell align="center">Capacity (kg)</TableCell>
                                            <TableCell align="center">Volume (cu.ft)</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell align="center">Driver</TableCell>
                                            <TableCell align="center">Insurance Expiry</TableCell>
                                            <TableCell align="center">Fitness Expiry</TableCell>
                                            <TableCell align="center">Pollution Expiry</TableCell>
                                            <TableCell align="center">Notes</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {showVehicles.length > 0 &&
                                            showVehicles.map((vehicle, index) => (
                                                <TableRow hover key={vehicle.id || index}>
                                                    <TableCell align="center">{vehicle.id}</TableCell>
                                                    <TableCell align="center">
                                                        <b>{vehicle.registrationNumber}</b>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {getVehicleTypeLabel(vehicle.vehicleType)}
                                                    </TableCell>
                                                    <TableCell align="center">{vehicle.make}</TableCell>
                                                    <TableCell align="center">{vehicle.model}</TableCell>
                                                    <TableCell align="center">{vehicle.color || '-'}</TableCell>
                                                    <TableCell align="center">{vehicle.capacityKg}</TableCell>
                                                    <TableCell align="center">{vehicle.capacityVolume || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        {getStatusLabel(vehicle.status)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {(vehicle.driver && vehicle.driver.name) || vehicle.driverId || '-'}
                                                    </TableCell>
                                                    <TableCell align="center">{formatDate(vehicle.insuranceExpiry)}</TableCell>
                                                    <TableCell align="center">{formatDate(vehicle.fitnessExpiry)}</TableCell>
                                                    <TableCell align="center">{formatDate(vehicle.pollutionExpiry)}</TableCell>
                                                    <TableCell align="center">{vehicle.notes || '-'}</TableCell>
                                                    <TableCell align="right" onClick={() => onClickAddEditData(vehicle)}>
                                                        <IconButton size="large" color="inherit" onClick={handleOpenMenu}>
                                                            <Iconify icon={'eva:more-vertical-fill'} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {showVehicles.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={15}
                                                    align="center"
                                                    style={{ height: '200px', verticalAlign: 'middle' }}
                                                >
                                                    <Typography variant="h6" color="text.secondary">
                                                        No data found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {showVehicles.length > 0 && (
                                <Stack alignItems="center">
                                    <Pagination
                                        color="primary"
                                        onChange={viewMoreResult}
                                        page={searchPageCount.current_page}
                                        count={searchPageCount.total_pages}
                                    />
                                </Stack>
                            )}
                            <Popover
                                open={Boolean(open)}
                                anchorEl={open}
                                onClose={handleCloseMenu}
                                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{
                                    sx: {
                                        p: 1,
                                        width: 140,
                                        '& .MuiMenuItem-root': {
                                            px: 1,
                                            typography: 'body2',
                                            borderRadius: 0.75,
                                        },
                                    },
                                }}
                            >
                                <MenuItem onClick={onClickEditVehicle}>
                                    <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
                                    Edit
                                </MenuItem>

                                <MenuItem sx={{ color: 'error.main' }}>
                                    <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
                                    Delete
                                </MenuItem>
                            </Popover>
                        </>
                    )}
                </Card>
            </Container>
        </>
    );
}
